package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.shared.model.displayTitle
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.shared.model.GenerationStatus
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.uikit.i18n.FriendlyErrors
import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Article
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.Event
import kotlin.js.Date

val AssetTileStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
            .borderRadius(16.px)
            .border(1.px, LineStyle.Solid, palette.border)
            .backgroundColor(palette.surface)
            .styleModifier { property("transition", "border-color 150ms, transform 150ms") }
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier.border(1.px, LineStyle.Solid, palette.borderStrong)
    }
}

/** One action in a tile's "more" menu. */
data class TileAction(val label: String, val icon: AppIcon, val danger: Boolean = false, val onClick: () -> Unit)

/**
 * Compact work card for the workshop grid: preview, type and status, the prompt as a title and the three main
 * actions (open, send to Studio, more). Everything else lives in the opened work's side panel.
 */
@Composable
fun AssetTile(
    asset: AssetResponse,
    onOpen: () -> Unit,
    onSendToStudio: (() -> Unit)?,
    moreActions: List<TileAction>,
    modifier: Modifier = Modifier
) {
    val s = strings()
    val palette = LocalAppTheme.current.colors
    Article(AssetTileStyle.toModifier().then(modifier).toAttrs()) {
        Button(
            Modifier
                .height(160.px)
                .fillMaxWidth()
                .padding(0.px)
                .border(0.px, LineStyle.None, Colors.Transparent)
                .styleModifier { property("border-bottom", "1px solid ${palette.border}") }
                .borderRadius(topLeft = 16.px, topRight = 16.px)
                .backgroundColor(palette.background)
                .position(Position.Relative)
                .overflow(Overflow.Hidden)
                .cursor(Cursor.Pointer)
                .color(palette.textSecondary)
                .onClick { onOpen() }
                .toAttrs { attr("type", "button"); attr("aria-label", "${s.home.open}: ${asset.displayTitle}") }
        ) {
            TilePreview(asset)
            SpanText(
                s.assetTypes.name(asset.assetType),
                Modifier
                    .position(Position.Absolute)
                    .top(12.px)
                    .left(12.px)
                    .height(24.px)
                    .padding(leftRight = 10.px)
                    .borderRadius(999.px)
                    .backgroundColor(palette.surface.toRgb().copy(alpha = 220))
                    .color(palette.textSecondary)
                    .fontSize(12.px)
                    .fontWeight(FontWeight.SemiBold)
                    .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                    .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
            )
        }
        Column(Modifier.padding(topBottom = 14.px, leftRight = 16.px).gap(10.px).flexGrow(1)) {
            Row(Modifier.gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                StatusChip(asset.status)
                SpanText(relativeTime(asset.timestamp), Modifier.fontSize(12.px).color(palette.textSecondary))
            }
            H3(
                Modifier
                    .margin(0.px)
                    .fontSize(15.px)
                    .fontWeight(FontWeight.Bold)
                    .lineHeight(1.35)
                    .color(palette.textPrimary)
                    .styleModifier {
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "2")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                    }
                    .toAttrs { attr("title", asset.prompt.take(300)) }
            ) { Text(asset.displayTitle) }
            if (asset.status == GenerationStatus.FAILED) {
                SpanText(
                    FriendlyErrors.describe(asset.errorMessage, s),
                    Modifier.fontSize(12.px).color(palette.statusFailed).lineHeight(1.4)
                )
            }
            Box(Modifier.flexGrow(1))
            Row(Modifier.gap(8.px).fillMaxWidth()) {
                AppButton(
                    text = s.home.open,
                    onClick = onOpen,
                    variant = AppButtonVariant.SECONDARY,
                    size = AppButtonSize.SM,
                    modifier = Modifier.flexGrow(1)
                )
                if (onSendToStudio != null && asset.status == GenerationStatus.READY) {
                    AppButton(
                        text = "",
                        icon = AppIcon.SEND,
                        ariaLabel = s.home.sendToStudio,
                        onClick = onSendToStudio,
                        variant = AppButtonVariant.SECONDARY,
                        size = AppButtonSize.SM,
                        modifier = Modifier.width(36.px).padding(0.px).title(s.home.sendToStudio)
                    )
                }
                if (moreActions.isNotEmpty()) MoreMenu(moreActions)
            }
        }
    }
}

@Composable
private fun TilePreview(asset: AssetResponse) {
    val palette = LocalAppTheme.current.colors
    val image = asset.metadata["previewImageUrl"] ?: asset.referenceImages.firstOrNull() ?: asset.metadata["conceptImageUrl"]
    when {
        asset.assetType == AssetType.LUAU_SCRIPT && !asset.luauCode.isNullOrBlank() -> CodePreview(asset.luauCode!!.lines().take(9).joinToString("\n"))
        asset.assetType == AssetType.ROJO_PROJECT && asset.files.isNotEmpty() ->
            CodePreview(asset.files.take(8).joinToString("\n") { it.path })
        asset.assetType == AssetType.MESH_3D && image != null && (image.startsWith("http") || image.startsWith("/") || image.startsWith("data:image")) ->
            Img(src = BackendEndpoint.resolveAssetUrl(image), attrs = Modifier.fillMaxWidth().fillMaxHeight().objectFit(ObjectFit.Cover).toAttrs { attr("alt", "") })
        else -> Box(Modifier.fillMaxWidth().fillMaxHeight(), contentAlignment = Alignment.Center) {
            Box(Modifier.color(when (asset.assetType) {
                AssetType.MESH_3D -> palette.statusProcessing
                AssetType.ANIMATION -> palette.statusReady
                else -> palette.textSecondary
            })) {
                Icon(
                    when (asset.assetType) {
                        AssetType.LUAU_SCRIPT -> AppIcon.CODE
                        AssetType.MESH_3D -> AppIcon.CUBE
                        AssetType.ANIMATION -> AppIcon.FIGURE
                        AssetType.ROJO_PROJECT -> AppIcon.FOLDER
                    },
                    size = 72,
                    strokeWidth = 1.2
                )
            }
        }
    }
}

@Composable
private fun CodePreview(text: String) {
    val palette = LocalAppTheme.current.colors
    Pre(
        Modifier
            .margin(0.px)
            .fillMaxWidth()
            .fillMaxHeight()
            .padding(topBottom = 44.px, leftRight = 16.px)
            .styleModifier {
                property("padding-top", "44px")
                property("box-sizing", "border-box")
                property("font-size", "12px")
                property("line-height", "1.6")
            }
            .color(palette.textSecondary)
            .whiteSpace(WhiteSpace.Pre)
            .overflow(Overflow.Hidden)
            .textAlign(TextAlign.Left)
            .toAttrs()
    ) { Text(text) }
}

@Composable
fun StatusChip(status: GenerationStatus) {
    val s = strings().assetCard
    val palette = LocalAppTheme.current.colors
    val (label, color) = when (status) {
        GenerationStatus.READY -> s.statusReady to palette.statusReady
        GenerationStatus.FAILED -> s.statusFailed to palette.statusFailed
        GenerationStatus.PENDING -> s.statusPending to palette.statusPending
        else -> s.statusStreaming to palette.statusProcessing
    }
    Row(
        Modifier
            .height(24.px)
            .padding(leftRight = 10.px)
            .borderRadius(999.px)
            .backgroundColor(color.toRgb().copy(alpha = 36))
            .color(color)
            .fontSize(12.px)
            .fontWeight(FontWeight.SemiBold)
            .gap(6.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(Modifier.size(7.px).borderRadius(50.percent).backgroundColor(color))
        SpanText(label)
    }
}

/** "2 min ago" for recent works, then the time today, then the date. */
@Composable
fun relativeTime(timestampMs: Long): String {
    if (timestampMs <= 0) return ""
    val s = strings().common
    val diffSec = ((Date.now() - timestampMs) / 1000).toInt().coerceAtLeast(0)
    val date = Date(timestampMs.toDouble())
    val now = Date()
    return when {
        diffSec < 60 -> s.agoSeconds(diffSec)
        diffSec < 3600 -> s.agoMinutes(diffSec / 60)
        date.toDateString() == now.toDateString() -> date.toLocaleTimeString(LocaleController.current.code, js("({hour: '2-digit', minute: '2-digit'})"))
        else -> date.toLocaleDateString(LocaleController.current.code)
    }
}

@Composable
internal fun MoreMenu(
    actions: List<TileAction>,
    openUp: Boolean = true,
    buttonSize: AppButtonSize = AppButtonSize.SM,
    // Grow the menu rightwards from the button's left edge, for buttons near the left of their container.
    alignStart: Boolean = false
) {
    val s = strings()
    val palette = LocalAppTheme.current.colors
    var open by remember { mutableStateOf(false) }
    var container by remember { mutableStateOf<HTMLElement?>(null) }

    DisposableEffect(open) {
        if (!open) return@DisposableEffect onDispose { }
        val outside: (Event) -> Unit = { event ->
            val target = event.target as? Node
            if (target != null && container?.contains(target) == false) open = false
        }
        val escape: (Event) -> Unit = { event -> if (event.asDynamic().key == "Escape") open = false }
        document.addEventListener("mousedown", outside)
        document.addEventListener("keydown", escape)
        onDispose {
            document.removeEventListener("mousedown", outside)
            document.removeEventListener("keydown", escape)
        }
    }

    Div(Modifier.position(Position.Relative).toAttrs { ref { element -> container = element; onDispose { container = null } } }) {
        AppButton(
            text = "",
            icon = AppIcon.MORE,
            ariaLabel = s.home.moreActions,
            onClick = { open = !open },
            variant = AppButtonVariant.SECONDARY,
            size = buttonSize,
            modifier = Modifier.width(if (buttonSize == AppButtonSize.SM) 36.px else 44.px).padding(0.px)
        )
        if (open) {
            Column(
                Modifier
                    .position(Position.Absolute)
                    .then(if (alignStart) Modifier.left(0.px) else Modifier.right(0.px))
                    .then(if (openUp) Modifier.bottom(if (buttonSize == AppButtonSize.SM) 42.px else 50.px) else Modifier.top(if (buttonSize == AppButtonSize.SM) 42.px else 50.px))
                    .minWidth(220.px)
                    .padding(6.px)
                    .borderRadius(12.px)
                    .border(1.px, LineStyle.Solid, palette.borderStrong)
                    .backgroundColor(palette.surface)
                    .styleModifier { property("box-shadow", "0 16px 40px rgba(0,0,0,0.45)") }
                    .zIndex(30)
                    .classNames("rc-fade-in")
            ) {
                actions.forEach { action ->
                    Button(
                        Modifier
                            .fillMaxWidth()
                            .minHeight(40.px)
                            .padding(leftRight = 10.px)
                            .borderRadius(8.px)
                            .border(0.px, LineStyle.None, Colors.Transparent)
                            .backgroundColor(Colors.Transparent)
                            .color(if (action.danger) palette.statusFailed else palette.textPrimary)
                            .fontSize(14.px)
                            .cursor(Cursor.Pointer)
                            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                            .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
                            .gap(10.px)
                            .styleModifier { property("font-family", "inherit") }
                            .onClick {
                                open = false
                                action.onClick()
                            }
                            .toAttrs { attr("type", "button"); attr("role", "menuitem") }
                    ) {
                        Icon(action.icon, size = 16)
                        SpanText(action.label)
                    }
                }
            }
        }
    }
}
